import React, { useEffect } from "react";
import { X, AlertTriangle } from "lucide-react";

const TYPE_LABEL = { table: "Table", barorder: "VIP Pre-Order", ticket: "Ticket" };

export const bookingTotal = (b) => Number(b.total_price_eur ?? b.price_eur ?? 0);

export function orderLines(b) {
  const items = b.bottle_items?.length ? b.bottle_items : b.bottles || [];
  const lines = items.map((it, i) => ({
    key: `${it.sku || it.name}-${i}`,
    qty: it.qty ?? 1,
    name: it.name || "Bottle",
    price: it.line_total_eur ?? it.total_eur ?? (it.unit_price_eur != null ? it.unit_price_eur * (it.qty ?? 1) : null),
  }));
  if (b.type === "table" && (b.table_name || b.table_package)) {
    const bottlesSum = lines.reduce((s, l) => s + Number(l.price || 0), 0);
    const pkg = bookingTotal(b) - bottlesSum;
    lines.unshift({ key: "pkg", qty: 1, name: `${b.table_name || b.table_package} table package`, price: pkg > 0 ? pkg : null });
  }
  if (b.type === "ticket" && b.quantity) {
    lines.push({ key: "tix", qty: b.quantity, name: `Ticket${b.event_title ? ` · ${b.event_title}` : ""}`, price: b.price_eur ?? null });
  }
  return lines;
}

export const isOrderMissing = (b) => (b.type === "table" || b.type === "barorder") && orderLines(b).length === 0;

export function OrderSummary({ b, testid }) {
  const lines = orderLines(b);
  if (!lines.length) {
    if (!isOrderMissing(b)) return null;
    return (
      <div data-testid={testid} className="mt-1 inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-orange-400 font-bold">
        <AlertTriangle size={12} /> No order saved
      </div>
    );
  }
  return (
    <ul data-testid={testid} className="mt-1 space-y-0.5">
      {lines.map((l) => (
        <li key={l.key} className="text-xs text-white/80">
          {l.qty}x {l.name}{l.price != null ? ` €${l.price}` : ""}
        </li>
      ))}
    </ul>
  );
}

function Row({ label, value, testid, strong }) {
  return (
    <div className="flex justify-between gap-4 py-2 border-b border-white/5 last:border-b-0">
      <span className="text-[10px] uppercase tracking-widest text-white/50 font-bold">{label}</span>
      <span data-testid={testid} className={`text-sm text-right break-all ${strong ? "text-neon font-bold" : "text-white"}`}>{value ?? "—"}</span>
    </div>
  );
}

export function BookingDetailModal({ booking: b, onClose }) {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  if (!b) return null;
  const lines = orderLines(b);
  const total = bookingTotal(b);
  const paid = b.deposit_eur ?? (b.payment_status === "paid" && b.type === "ticket" ? total : 0);
  const balance = b.balance_eur ?? Math.max(0, total - Number(paid || 0));
  const money = b.type !== "ticket" || total > 0;

  return (
    <div className="fixed inset-0 z-[90] bg-black/80 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose} data-testid="booking-detail-modal">
      <div className="w-full sm:max-w-lg max-h-[92dvh] overflow-y-auto bg-[#0b0710] border border-neon/40 rounded-t-3xl sm:rounded-3xl p-5 sm:p-6 shadow-[0_0_40px_rgba(255,27,141,0.25)]" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0 flex-1">
            <div className="text-[10px] uppercase tracking-widest text-neon font-bold">{TYPE_LABEL[b.type] || b.type}</div>
            <h2 className="font-heading text-2xl sm:text-3xl uppercase mt-1 break-words" data-testid="booking-detail-name">{b.name}</h2>
          </div>
          <button type="button" onClick={onClose} data-testid="booking-detail-close" aria-label="Close" className="shrink-0 p-2 rounded-full border border-white/15 hover:border-neon text-white/70 hover:text-white">
            <X size={18} />
          </button>
        </div>

        <div className="mt-4">
          <Row label="Email" value={b.email} testid="booking-detail-email" />
          <Row label="WhatsApp" value={b.whatsapp} testid="booking-detail-whatsapp" />
          <Row label="Date" value={b.date} testid="booking-detail-date" />
          {b.arrival_time && <Row label="Arrival" value={b.arrival_time} testid="booking-detail-arrival" />}
          {b.party_size != null && <Row label="Guests" value={b.party_size} testid="booking-detail-party" />}
          {b.event_title && <Row label="Event" value={b.event_title} testid="booking-detail-event" />}
          <Row label="Status" value={b.status} testid="booking-detail-status" />
          {b.payment_status && <Row label="Payment" value={b.payment_status} testid="booking-detail-payment-status" />}
          <Row label="Booked" value={b.created_at ? new Date(b.created_at).toLocaleString() : null} testid="booking-detail-created" />
          <Row label="Reference" value={b.id} testid="booking-detail-id" />
        </div>

        <div className="mt-5">
          <div className="text-[10px] uppercase tracking-widest text-white/50 font-bold mb-2">Order</div>
          {lines.length ? (
            <ul className="rounded-2xl border border-white/10 divide-y divide-white/5" data-testid="booking-detail-order">
              {lines.map((l) => (
                <li key={l.key} className="flex justify-between px-4 py-2 text-sm">
                  <span>{l.qty}x {l.name}</span>
                  <span className="text-white/80">{l.price != null ? `€${l.price}` : ""}</span>
                </li>
              ))}
            </ul>
          ) : (
            <div data-testid="booking-detail-order-missing" className="rounded-2xl border border-orange-400/40 px-4 py-3 text-sm text-orange-300 flex items-center gap-2">
              <AlertTriangle size={14} /> No order was saved on this booking.
            </div>
          )}
        </div>

        {money && (
          <div className="mt-5">
            <Row label="Total" value={`€${total}`} testid="booking-detail-total" />
            <Row label="Paid" value={`€${paid ?? 0}`} testid="booking-detail-paid" strong />
            <Row label="Balance left" value={`€${balance}`} testid="booking-detail-balance" />
          </div>
        )}

        {b.notes && (
          <div className="mt-5">
            <div className="text-[10px] uppercase tracking-widest text-white/50 font-bold mb-2">Notes</div>
            <p className="text-sm text-white/80 whitespace-pre-line" data-testid="booking-detail-notes">{b.notes}</p>
          </div>
        )}
      </div>
    </div>
  );
}
